/** Zoom do PDF (Fase 10 US-06) — geometria pura.
 *
 * O zoom aqui é **escala + rolagem nativa**, não recorte: uma escala só vale
 * para o canvas e para a camada de texto, e o container rola. Recortar exigiria
 * manter uma transformação própria e refazer o alinhamento da camada de texto a
 * cada gesto — que é exatamente o cálculo que o `--scale-factor` já faz de
 * graça quando a escala é única.
 *
 * Todas as coordenadas de entrada são NORMALIZADAS (0..1) sobre a página, como
 * manda a RN-04: assim região de zoom e destaque não dependem de DPI, de
 * densidade de tela nem do zoom corrente.
 */
import {
  ESCALA_MAX,
  cabeNoOrcamento,
  dprEfetivo,
  limitarEscala,
  type Tamanho,
} from '@/features/reader/utils/scale'
import type { Rect } from '@/types/api'

/** Passo dos botões de zoom. 25% é o menor passo que se percebe numa página. */
export const PASSO = 0.25

/** Piso: abaixo disso a página não é lida, é só vista. */
export const ESCALA_MIN = 0.5

/** Folga em volta da região, para o aluno ver que há página em volta. */
const MARGEM = 0.06

/** Escala que faz a região preencher o container, com uma folga.
 *
 * Usa o MENOR dos dois ajustes (largura e altura): o maior faria a região
 * transbordar no outro eixo, e uma tabela ampliada com as linhas de baixo fora
 * da tela é pior que uma tabela um pouco menor inteira.
 */
export function escalaParaRegiao(
  regiao: Rect,
  pagina: Tamanho,
  container: Tamanho,
): number {
  const larguraRegiao = Math.max(regiao.w, 0.01) * pagina.width * (1 + MARGEM)
  const alturaRegiao = Math.max(regiao.h, 0.01) * pagina.height * (1 + MARGEM)
  if (container.width <= 0) return 1

  const porLargura = container.width / larguraRegiao
  const porAltura = container.height > 0 ? container.height / alturaRegiao : porLargura
  return limitarEscala(Math.max(ESCALA_MIN, Math.min(porLargura, porAltura)))
}

/** Centro de um retângulo normalizado — o ponto para onde a rolagem vai. */
export function centroDe(regiao: Rect): { x: number; y: number } {
  return { x: regiao.x + regiao.w / 2, y: regiao.y + regiao.h / 2 }
}

/** Escala do clique-para-ampliar quando o PDF não tem regiões (fallback US-06).
 *
 * Adaptada ao viewport, não fixa em "2×": num celular de 390px a página já foi
 * desenhada pequena para caber, então dobrar ainda deixa a tabela ilegível; num
 * monitor largo, dobrar estoura a tela sem necessidade. A conta é "quanto falta
 * para uma largura de leitura confortável", com teto.
 */
export function escalaDeCliqueAdaptativa(
  escalaAtual: number,
  pagina: Tamanho,
  container: Tamanho,
  larguraConfortavel = 1100,
): number {
  if (pagina.width <= 0 || container.width <= 0) return escalaAtual
  const alvo = Math.max(container.width, larguraConfortavel) / pagina.width
  // Nunca reduzir: quem clica para ampliar espera ampliar.
  return limitarEscala(Math.max(escalaAtual * 1.5, alvo))
}

/** Aplica o passo dos botões, com piso e teto. */
export function comPasso(escala: number, passos: number): number {
  const nova = escala + passos * PASSO
  return Math.min(ESCALA_MAX, Math.max(ESCALA_MIN, Number(nova.toFixed(4))))
}

/** Maior escala que ainda cabe no orçamento de pixels do canvas.
 *
 * ⚠️ A cascata é a mesma do render: primeiro a DENSIDADE cede (3 → 2 → 1), e só
 * depois a escala. O Safari no iPhone recusa canvas acima de ~16,7 MPx
 * devolvendo canvas em BRANCO, sem erro — o aluno vê a página sumir e não há o
 * que depurar. Melhor entregar menos nitidez que nenhuma página.
 */
export function limitarPeloOrcamento(
  pagina: Tamanho,
  escala: number,
  dprDesejado = 1,
): number {
  const dpr = dprEfetivo(pagina.width * escala, pagina.height * escala, dprDesejado)
  let candidata = limitarEscala(escala)
  while (candidata > ESCALA_MIN && !cabeNoOrcamento(pagina, candidata, dpr)) {
    candidata = Number((candidata - 0.1).toFixed(4))
  }
  return Math.max(ESCALA_MIN, candidata)
}

/** Rolagem que põe o ponto `foco` (normalizado) no centro da área visível.
 *
 * Devolve a posição, em vez de aplicá-la: o cálculo fica testável em jsdom, que
 * não faz layout e devolveria zero em qualquer medida real.
 */
export function offsetDeFoco(
  foco: { x: number; y: number },
  paginaEmPx: Tamanho,
  visivel: Tamanho,
): { left: number; top: number } {
  return {
    left: Math.max(0, foco.x * paginaEmPx.width - visivel.width / 2),
    top: Math.max(0, foco.y * paginaEmPx.height - visivel.height / 2),
  }
}

/** Ponto normalizado de um clique dentro da caixa da página. */
export function focoDoClique(
  clique: { clientX: number; clientY: number },
  caixa: { left: number; top: number; width: number; height: number },
): { x: number; y: number } | null {
  if (caixa.width <= 0 || caixa.height <= 0) return null
  const x = (clique.clientX - caixa.left) / caixa.width
  const y = (clique.clientY - caixa.top) / caixa.height
  if (x < 0 || x > 1 || y < 0 || y > 1) return null
  return { x, y }
}
